import type { DetectionBox, DetectionCategory, LocalAiReview } from "@aimosaic/core";

export type CandidateStrength = "strong" | "weak";

export interface LocalAiReviewStats {
  reviewed: number;
  dropped: number;
  changed: number;
  needsReview: number;
}

export interface LocalAiReviewResult {
  boxes: DetectionBox[];
  stats: LocalAiReviewStats;
}

export function applyLocalAiReviews(
  boxes: DetectionBox[],
  reviews: LocalAiReview[],
  enabledCategories: readonly DetectionCategory[]
): LocalAiReviewResult {
  const enabled = new Set(enabledCategories);
  const reviewById = new Map(reviews.map((review) => [review.boxId, review]));
  const stats: LocalAiReviewStats = { reviewed: 0, dropped: 0, changed: 0, needsReview: 0 };
  const nextBoxes: DetectionBox[] = [];

  for (const box of boxes) {
    const review = reviewById.get(box.id);
    if (!review) {
      if (classifyCandidateStrength(box) === "weak") {
        stats.dropped += 1;
        continue;
      }
      if (box.requiresReview) stats.needsReview += 1;
      nextBoxes.push(box);
      continue;
    }

    stats.reviewed += 1;

    if (review.decision === "drop") {
      stats.dropped += 1;
      continue;
    }

    if (review.decision === "change") {
      if (!review.category || !enabled.has(review.category)) {
        nextBoxes.push(box);
        continue;
      }
      stats.changed += 1;
      pushReviewedBox(nextBoxes, stats, applyReviewMetadata({ ...box, category: review.category, source: "local-llm" }, review));
      continue;
    }

    pushReviewedBox(nextBoxes, stats, applyReviewMetadata(box, review));
  }

  return { boxes: nextBoxes, stats };
}

export function shouldReviewCandidate(box: DetectionBox): boolean {
  if (box.source !== "local-ocr") return false;
  if (classifyCandidateStrength(box) === "weak") return true;
  if (box.category === "ip") return true;
  return Boolean(box.requiresReview);
}

export function classifyCandidateStrength(box: DetectionBox): CandidateStrength {
  if (box.source !== "local-ocr") return "strong";
  if (box.confidence < 0.7) return "weak";
  if (isWeakReason(box.reason)) return "weak";
  if (box.category === "url" && looksLikeLocalFileReference(box.text ?? "")) return "weak";
  return "strong";
}

function applyReviewMetadata(box: DetectionBox, review: LocalAiReview): DetectionBox {
  return {
    ...box,
    confidence: review.confidence ?? box.confidence,
    reason: review.reason ?? box.reason,
    requiresReview: review.requiresReview ?? box.requiresReview
  };
}

function pushReviewedBox(boxes: DetectionBox[], stats: LocalAiReviewStats, box: DetectionBox): void {
  if (box.requiresReview) stats.needsReview += 1;
  boxes.push(box);
}

function isWeakReason(reason: string | undefined): boolean {
  const normalized = reason?.toLowerCase() ?? "";
  return (
    normalized.includes("ocr recovery") ||
    normalized.includes("repeated-row layout recovery") ||
    normalized.includes("layout recovery")
  );
}

function looksLikeLocalFileReference(text: string): boolean {
  const value = text.trim();
  if (!value) return false;
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(value)) return false;
  if (/^\/(?:Users|Volumes|private|tmp|var)\//i.test(value)) return true;
  if (/^[A-Z]:\\/i.test(value)) return true;
  return /\.(?:txt|md|markdown|png|jpe?g|webp|gif|pdf|docx?|xlsx?|csv|log|json|ya?ml)$/i.test(value);
}
